/* eslint-disable react/jsx-no-bind */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  CalendarTodayOutlined,
  CheckCircleOutline,
  Close,
  LocationOnOutlined,
  PhotoCameraOutlined,
  VideocamOutlined,
} from '@mui/icons-material';
import { MEDIA_TYPES } from '../Task.model';
import colors from '../../colors.styles';
import adminFormStyles from '../../shared/styles/adminForm.styles';

const MOCK_TARGETING = {
  locations: ['NY', 'PA', 'MOCK'],
  storesTargeted: 240,
  totalStores: 4200,
  associatesTargeted: 12453,
};

const styles = {
  paper: {
    border: '1px solid #dbe2e6',
    borderRadius: '12px',
    boxShadow: '0 20px 50px rgba(4, 31, 65, 0.18)',
    width: '100%',
  },
  header: {
    alignItems: 'flex-start',
    borderBottom: '1px solid #e9ecee',
    display: 'flex',
    gap: 2,
    p: '20px',
    position: 'relative',
  },
  statusIcon: {
    alignItems: 'center',
    backgroundColor: '#e5eefc',
    borderRadius: '50%',
    color: colors.base.walmart_blue,
    display: 'flex',
    flexShrink: 0,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  statusIconGlyph: {
    fontSize: 22,
  },
  title: {
    color: colors.base.bentonville_blue,
    fontSize: '20px',
    fontWeight: 800,
    lineHeight: 1.2,
  },
  subtitle: {
    color: '#656d80',
    fontSize: '14px',
    mt: 0.75,
  },
  closeButton: {
    color: '#656d80',
    position: 'absolute',
    right: 12,
    top: 12,
  },
  content: {
    p: { xs: '20px', sm: '20px 28px 28px' },
  },
  columns: {
    display: 'grid',
    gap: { xs: 3, md: 5 },
    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  },
  detailGroup: {
    minWidth: 0,
  },
  label: {
    color: '#656d80',
    fontSize: '14px',
    mb: 0.75,
  },
  value: {
    color: colors.base.bentonville_blue,
    fontSize: '14px',
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
  },
  titleValue: {
    color: colors.base.bentonville_blue,
    fontSize: '16px',
    fontWeight: 800,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
  },
  deadline: {
    alignItems: 'center',
    color: colors.base.bentonville_blue,
    display: 'flex',
    fontSize: '14px',
    gap: 1.25,
  },
  deadlineIcon: {
    fontSize: 16,
  },
  contentItem: {
    alignItems: 'center',
    backgroundColor: '#f6f6f6',
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    display: 'flex',
    gap: 1.5,
    minWidth: 0,
    p: '12px',
  },
  itemNumber: {
    alignItems: 'center',
    backgroundColor: colors.base.walmart_blue,
    borderRadius: '50%',
    color: colors.base.light,
    display: 'flex',
    flexShrink: 0,
    fontSize: '12px',
    fontWeight: 800,
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
  itemText: {
    flex: 1,
    minWidth: 0,
  },
  itemTitle: {
    color: colors.base.bentonville_blue,
    fontSize: '16px',
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemDescription: {
    color: '#656d80',
    display: '-webkit-box',
    fontSize: '14px',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  },
  mediaType: {
    alignItems: 'center',
    backgroundColor: colors.base.light,
    border: '1px solid #e9ecee',
    borderRadius: '8px',
    color: '#656d80',
    display: 'flex',
    flexShrink: 0,
    fontSize: '14px',
    gap: 0.75,
    px: 1.5,
    py: 0.5,
  },
  mediaTypeIcon: {
    fontSize: 14,
  },
  targetingChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1.25,
  },
  targetingChip: {
    alignItems: 'center',
    backgroundColor: colors.base.walmart_blue,
    borderRadius: '20px',
    color: colors.base.light,
    display: 'flex',
    fontSize: '14px',
    gap: 0.75,
    px: 2,
    py: 1,
  },
  targetingIcon: {
    fontSize: 14,
  },
  targetingSummary: {
    backgroundColor: '#f6f6f6',
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    mt: 1.25,
    p: '12px',
  },
  targetingMetric: {
    minWidth: 0,
    px: 0.25,
    '& + &': {
      borderLeft: '1px solid #dbe2e6',
      pl: 1.75,
    },
  },
  metricValue: {
    color: colors.base.bentonville_blue,
    fontSize: '22px',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  metricTotal: {
    color: '#656d80',
    fontSize: '14px',
    fontWeight: 400,
    ml: 0.5,
  },
  metricLabel: {
    color: '#656d80',
    fontSize: '14px',
    mt: 0.25,
  },
  actions: {
    borderTop: '1px solid #e9ecee',
    gap: 1,
    p: '16px 20px',
  },
  button: {
    ...adminFormStyles.secondaryButton,
    color: colors.base.bentonville_blue,
  },
  confirmButton: adminFormStyles.primaryButton,
};

function MediaType({ type }) {
  const isVideo = type === MEDIA_TYPES.VIDEO;

  return (
    <Box sx={styles.mediaType}>
      {isVideo ? (
        <VideocamOutlined sx={styles.mediaTypeIcon} />
      ) : (
        <PhotoCameraOutlined sx={styles.mediaTypeIcon} />
      )}
      {isVideo ? 'Video' : 'Photo'}
    </Box>
  );
}

MediaType.propTypes = {
  type: PropTypes.oneOf(Object.values(MEDIA_TYPES)).isRequired,
};

export default function TaskLaunchModal({
  isLaunching,
  isOpen,
  onClose,
  onConfirm,
  task,
}) {
  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={isOpen}
      onClose={isLaunching ? undefined : onClose}
      PaperProps={{ sx: styles.paper }}
      aria-labelledby="task-launch-title"
      aria-describedby="task-launch-description"
    >
      <Box sx={styles.header}>
        <Box sx={styles.statusIcon}>
          <CheckCircleOutline sx={styles.statusIconGlyph} />
        </Box>
        <Box>
          <Typography component="h2" id="task-launch-title" sx={styles.title}>
            Ready to go live?
          </Typography>
          <Typography id="task-launch-description" sx={styles.subtitle}>
            Review your request before posting it to stores.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          disabled={isLaunching}
          onClick={onClose}
          size="small"
          sx={styles.closeButton}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={styles.content}>
        <Box sx={styles.columns}>
          <Stack spacing={2.5}>
            <Box sx={styles.detailGroup}>
              <Typography sx={styles.label}>Title</Typography>
              <Typography sx={styles.titleValue}>{task.title}</Typography>
            </Box>
            <Box sx={styles.detailGroup}>
              <Typography sx={styles.label}>Deadline</Typography>
              <Box sx={styles.deadline}>
                <CalendarTodayOutlined sx={styles.deadlineIcon} />
                {moment(task.due_date).format('MM/DD/YYYY')}
              </Box>
            </Box>
            <Box sx={styles.detailGroup}>
              <Typography sx={styles.label}>Description</Typography>
              <Typography sx={styles.value}>{task.description}</Typography>
            </Box>
          </Stack>

          <Stack spacing={2.5}>
            <Box>
              <Typography sx={styles.label}>
                Content items ({task.content_requests.length})
              </Typography>
              <Stack spacing={1.5}>
                {task.content_requests.map((contentRequest, index) => (
                  <Box key={contentRequest.ui_id} sx={styles.contentItem}>
                    <Box sx={styles.itemNumber}>{index + 1}</Box>
                    <Box sx={styles.itemText}>
                      <Typography sx={styles.itemTitle}>
                        {contentRequest.title}
                      </Typography>
                      <Typography sx={styles.itemDescription}>
                        {contentRequest.description}
                      </Typography>
                    </Box>
                    <MediaType type={contentRequest.media_type} />
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography sx={styles.label}>Targeting</Typography>
              <Box sx={styles.targetingChips}>
                {MOCK_TARGETING.locations.map((location) => (
                  <Box key={location} sx={styles.targetingChip}>
                    <LocationOnOutlined sx={styles.targetingIcon} />
                    {location}
                  </Box>
                ))}
              </Box>
              <Box sx={styles.targetingSummary}>
                <Box sx={styles.targetingMetric}>
                  <Typography sx={styles.metricValue}>
                    {MOCK_TARGETING.storesTargeted.toLocaleString()}
                    <Box component="span" sx={styles.metricTotal}>
                      of {MOCK_TARGETING.totalStores.toLocaleString()}
                    </Box>
                  </Typography>
                  <Typography sx={styles.metricLabel}>
                    stores targeted
                  </Typography>
                </Box>
                <Box sx={styles.targetingMetric}>
                  <Typography sx={styles.metricValue}>
                    {MOCK_TARGETING.associatesTargeted.toLocaleString()}
                  </Typography>
                  <Typography sx={styles.metricLabel}>
                    associates targeted
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button
          disabled={isLaunching}
          onClick={onClose}
          sx={styles.button}
          variant="outlined"
        >
          Go back
        </Button>
        <Button
          disabled={isLaunching}
          onClick={onConfirm}
          startIcon={
            isLaunching ? <CircularProgress color="inherit" size={16} /> : null
          }
          sx={styles.confirmButton}
          variant="contained"
        >
          {isLaunching ? 'Setting live...' : 'Set request live'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TaskLaunchModal.propTypes = {
  isLaunching: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  task: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    due_date: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    content_requests: PropTypes.arrayOf(
      PropTypes.shape({
        ui_id: PropTypes.string.isRequired,
        title: PropTypes.string,
        description: PropTypes.string,
        media_type: PropTypes.oneOf(Object.values(MEDIA_TYPES)),
      })
    ).isRequired,
  }).isRequired,
};

TaskLaunchModal.defaultProps = {
  isLaunching: false,
};
